import jsPDF from 'jspdf';
import { formatDate } from './helpers';

const formatAmount = (amount, currency = 'CLP') => {
  if (amount == null) return '-';
  const numericValue = Number(amount);
  if (Number.isNaN(numericValue)) return '-';
  try {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0
    }).format(numericValue);
  } catch (error) {
    return `${numericValue.toLocaleString('es-CL')} ${currency}`;
  }
};

const addHeader = (doc, title) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(title, 40, 50);
  doc.setDrawColor(148, 163, 184);
  doc.setLineWidth(0.8);
  doc.line(40, 58, 555, 58);
  doc.setFontSize(10);
};

const ensurePageSpace = (doc, currentY, threshold = 760) => {
  if (currentY > threshold) {
    doc.addPage();
    return 60;
  }
  return currentY;
};

export const generatePurchaseOrderPdf = (order, context = {}) => {
  if (!order) return;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  addHeader(doc, 'Orden de Compra');

  const supplierName = order.supplierName || context.supplierName || 'Sin proveedor';
  const locationName = context.locationName || 'No definido';
  const parkName = context.parkName || 'Sin parque asignado';
  const generatedBy = context.generatedBy || 'Sistema';

  doc.setFontSize(11);
  let currentY = 80;
  const metadata = [
    [`Folio`, order.id || '-'],
    [`Estado`, order.status || '-'],
    [`Proveedor`, supplierName],
    [`Centro de costo`, context.costCenter || '-'],
    [`Parque / Proyecto`, parkName],
    [`Entrega en`, locationName],
    [`Fecha estimada`, order.deliveryDate ? formatDate(order.deliveryDate) : 'Por definir'],
    [`Creado por`, generatedBy]
  ];

  metadata.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, 40, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(typeof value === 'string' ? value : String(value), 180, currentY);
    currentY += 16;
  });

  currentY += 12;
  currentY = ensurePageSpace(doc, currentY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Detalle de ítems', 40, currentY);
  currentY += 18;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('SKU', 40, currentY);
  doc.text('Descripción', 100, currentY);
  doc.text('Cantidad', 340, currentY);
  doc.text('Valor unitario', 410, currentY);
  doc.text('Subtotal', 500, currentY);
  doc.setFont('helvetica', 'normal');
  currentY += 14;

  (order.items || []).forEach((item) => {
    currentY = ensurePageSpace(doc, currentY);
    const description = doc.splitTextToSize(item.description || '-', 220);
    const lines = description.length;
    doc.text(item.sku || '-', 40, currentY);
    doc.text(description, 100, currentY);
    doc.text(String(item.quantity || 0), 340, currentY);
    doc.text(formatAmount(item.unitPrice || 0, order.currency || 'CLP'), 410, currentY);
    doc.text(
      formatAmount((item.quantity || 0) * (item.unitPrice || 0), order.currency || 'CLP'),
      500,
      currentY
    );
    currentY += lines * 12 + 6;
  });

  currentY += 10;
  doc.setFont('helvetica', 'bold');
  doc.text(
    `Total OC: ${formatAmount(order.totalValue || 0, order.currency || 'CLP')}`,
    40,
    currentY
  );
  currentY += 20;

  if (order.notes) {
    doc.setFont('helvetica', 'bold');
    doc.text('Notas / Comentarios:', 40, currentY);
    doc.setFont('helvetica', 'normal');
    const noteLines = doc.splitTextToSize(order.notes, 480);
    currentY += 14;
    doc.text(noteLines, 40, currentY);
    currentY += noteLines.length * 12 + 10;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Documento generado desde Alcor ERP Demo.', 40, 810);
  doc.save(`${order.id || 'orden-compra'}.pdf`);
};

export const generatePurchaseRequisitionPdf = (requisition, context = {}) => {
  if (!requisition) return;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  addHeader(doc, 'Solicitud de Compra');

  const requester = context.requester || requisition.requesterName || requisition.requesterId || 'Sin solicitante';
  const parkName = context.parkName || 'Sin parque asignado';
  const areaName = context.areaName || 'Área no definida';
  const costCenter = context.costCenter || '-';

  doc.setFontSize(11);
  let currentY = 80;
  const metadata = [
    ['Folio', requisition.id || '-'],
    ['Estado', requisition.status || '-'],
    ['Solicitante', requester],
    ['Parque', parkName],
    ['Área', areaName],
    ['Centro de costo', costCenter],
    ['Fecha requerida', requisition.neededDate ? formatDate(requisition.neededDate) : 'Por definir'],
    ['Tipo', requisition.type || '-']
  ];

  metadata.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, 40, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(typeof value === 'string' ? value : String(value), 190, currentY);
    currentY += 16;
  });

  if (requisition.justification) {
    currentY += 10;
    doc.setFont('helvetica', 'bold');
    doc.text('Justificación:', 40, currentY);
    currentY += 14;
    doc.setFont('helvetica', 'normal');
    const justificationLines = doc.splitTextToSize(requisition.justification, 500);
    doc.text(justificationLines, 40, currentY);
    currentY += justificationLines.length * 12 + 10;
  }

  currentY = ensurePageSpace(doc, currentY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Ítems solicitados', 40, currentY);
  currentY += 18;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Descripción', 40, currentY);
  doc.text('Cantidad', 340, currentY);
  doc.text('Unidad', 420, currentY);
  doc.setFont('helvetica', 'normal');
  currentY += 14;

  (requisition.lines || []).forEach((line) => {
    currentY = ensurePageSpace(doc, currentY);
    const description = doc.splitTextToSize(line.description || '-', 280);
    const lines = description.length;
    doc.text(description, 40, currentY);
    doc.text(String(line.quantity || 0), 340, currentY);
    doc.text(line.unit || '-', 420, currentY);
    currentY += lines * 12 + 6;
  });

  currentY += 10;
  doc.setFont('helvetica', 'bold');
  doc.text('Historial de aprobaciones', 40, currentY);
  currentY += 16;
  doc.setFont('helvetica', 'normal');
  (requisition.history || []).forEach((entry) => {
    currentY = ensurePageSpace(doc, currentY);
    doc.text(`- ${entry.action} · ${entry.status || ''} · ${entry.timestamp || ''}`, 40, currentY);
    currentY += 14;
  });

  doc.setFontSize(9);
  doc.text('Documento generado desde Alcor ERP Demo.', 40, 810);
  doc.save(`${requisition.id || 'solicitud-compra'}.pdf`);
};
